import { useState } from 'react';
import { AppDialog, AppDialogClose } from '../../../components/common/CustomMdal';
import { Api } from '../../../services/interceptor';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import type { CheckoutRequest, CheckoutResponse } from '../types/subscription';
import toast from 'react-hot-toast';
import './checkoutModal.scss';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  planId: string;
  planName: string;
  planPrice: string;
  currency: string;
}

const CheckoutModal = ({ isOpen, onClose, planId, planName, planPrice, currency }: CheckoutModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const baseUrl = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  const basePathWithSlash = `${baseUrl}/`.replace(/\/\/+/, '/');

  const buildReturnUrl = (status: 'success' | 'cancelled') =>
    `${window.location.origin}${basePathWithSlash}#/pricing?checkout=${status}`;

  const handleCheckout = async () => {
    if (!planId) {
      toast.error('Invalid plan selected');
      return;
    }

    if (!isConsentChecked) {
      toast.error('Please confirm consent to proceed');
      return;
    }

    setIsLoading(true);

    try {
      const checkoutRequest: CheckoutRequest = {
        chargeBeePlanId: planId,
        redirectUrl: buildReturnUrl('success'),
        cancelUrl: buildReturnUrl('cancelled'),
        currency,
      };

      const response = await Api.post<CheckoutResponse>(
        QUERY_KEY.createCheckoutSession,
        checkoutRequest
      );

      if (response.data.success && response.data.data.url) {
        // Store plan price in user's currency for success popup after redirect
        try {
          window.localStorage.setItem('checkout_plan_meta', JSON.stringify({ planPrice, planName }));
        } catch {
          // Ignore storage errors
        }
        // Redirect to Chargebee checkout page
        window.location.href = response.data.data.url;
      } else {
        toast.error(response.data.message || 'Failed to create checkout session');
        setIsLoading(false);
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to start checkout process. Please try again.';
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <AppDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          onClose();
        }
      }}
      size="md"
      className="checkout-modal"
      showCloseIcon={false}
    >
      <div className="checkout-modal-content">
        <div className="checkout-header">
          <h2 className="checkout-title">Are you sure you want to proceed with the payment?</h2>
          {!isLoading && (
            <AppDialogClose className="checkout-close" aria-label="Close">
              ✕
            </AppDialogClose>
          )}
        </div>

        <div className="checkout-body">
          <div className="checkout-plan-summary">
            <p><strong>{planName}</strong> — {planPrice}</p>
          </div>
          <div className="checkout-note">
            <p>Please ensure all card and payment details entered are correct.</p>
          </div>

          <label className="checkout-consent">
            <input
              type="checkbox"
              checked={isConsentChecked}
              onChange={(e) => setIsConsentChecked(e.target.checked)}
              disabled={isLoading}
            />
            <span>I confirm and give my consent to proceed with this payment.</span>
          </label>
        </div>

        <div className="checkout-footer">
          <button
            className={`checkout-proceed-button ${isConsentChecked && !isLoading ? 'is-enabled' : ''}`}
            onClick={handleCheckout}
            disabled={isLoading || !isConsentChecked}
            title={`Proceed with ${planName} (${planPrice})`}
            aria-label={`Proceed with ${planName} plan for ${planPrice}`}
          >
            {isLoading ? 'Processing...' : 'Proceed to Checkout'}
          </button>
        </div>
      </div>
    </AppDialog>
  );
};

export default CheckoutModal;

